import React, { useCallback, useEffect, useRef, useState } from "react";
import { NOTIFY_EVENT } from "../utils/notify";
import "./ApiNotices.css";

// Renders notices raised through utils/notify as small, auto-dismissing, non-blocking toasts (never a browser alert).
// Errors are announced assertively, warnings and info politely. A notice stays while hovered or focused, can be dismissed
// with its button or Escape, and a notice with the key of one already visible replaces it instead of stacking.
const VISIBLE_MS = 6000;
const MAX_VISIBLE = 3;

const TEXT = {
  en: { region: "Notifications", dismiss: "Dismiss", reference: "Reference" },
  ar: { region: "الإشعارات", dismiss: "إغلاق", reference: "المرجع" },
};

const textOf = (message, lang) =>
  typeof message === "string" ? message : message?.[lang] || message?.en || "";

export function ApiNoticeHost({ lang = "en" }) {
  const [items, setItems] = useState([]);
  const timers = useRef(new Map());
  const paused = useRef(new Set());

  const dismiss = useCallback((id) => {
    const timer = timers.current.get(id);
    if (timer) clearTimeout(timer);
    timers.current.delete(id);
    paused.current.delete(id);
    setItems((current) => current.filter((item) => item.id !== id));
  }, []);

  const schedule = useCallback(
    (id) => {
      const existing = timers.current.get(id);
      if (existing) clearTimeout(existing);
      timers.current.set(id, setTimeout(() => (paused.current.has(id) ? null : dismiss(id)), VISIBLE_MS));
    },
    [dismiss],
  );

  useEffect(() => {
    const allTimers = timers.current;
    const onNotify = (event) => {
      const detail = event.detail;
      if (!detail?.message) return;
      // Same key: the new notice replaces the old one. A timer of a replaced or evicted notice later dismisses nothing.
      setItems((current) =>
        [...current.filter((item) => !(detail.key && item.key === detail.key)), detail].slice(-MAX_VISIBLE),
      );
      schedule(detail.id);
    };
    window.addEventListener(NOTIFY_EVENT, onNotify);
    return () => {
      window.removeEventListener(NOTIFY_EVENT, onNotify);
      allTimers.forEach(clearTimeout);
      allTimers.clear();
    };
  }, [schedule]);

  const text = TEXT[lang] || TEXT.en;
  const pause = (id) => paused.current.add(id);
  const resume = (id) => {
    paused.current.delete(id);
    schedule(id);
  };

  // The live regions exist before any notice, so screen readers announce the first one too.
  const render = (level) =>
    items
      .filter((item) => (level === "error" ? item.level === "error" || !item.level : item.level === level))
      .map((item) => (
        <div
          key={item.id}
          className={`api-notice api-notice--${item.level || "error"}`}
          onMouseEnter={() => pause(item.id)}
          onMouseLeave={() => resume(item.id)}
          onFocus={() => pause(item.id)}
          onBlur={() => resume(item.id)}
          onKeyDown={(event) => event.key === "Escape" && dismiss(item.id)}
          data-testid="api-notice"
        >
          <span className="api-notice__text">
            {textOf(item.message, lang)}
            {item.requestId ? (
              <small className="api-notice__reference">
                {text.reference}: <bdi>{item.requestId}</bdi>
              </small>
            ) : null}
          </span>
          <button type="button" className="api-notice__dismiss" aria-label={text.dismiss} onClick={() => dismiss(item.id)}>
            ×
          </button>
        </div>
      ));

  return (
    <div className="api-notices" dir={lang === "ar" ? "rtl" : "ltr"} lang={lang} role="region" aria-label={text.region}>
      <div role="alert" aria-live="assertive" aria-atomic="false" className="api-notices__stack">
        {render("error")}
      </div>
      <div role="status" aria-live="polite" aria-atomic="false" className="api-notices__stack">
        {render("warning")}
        {render("info")}
      </div>
    </div>
  );
}

export default ApiNoticeHost;
